import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setRestrictedExam, setRestrictedMode } from "../store/reducers/user.reducer";
import { useQueryClient } from "react-query";
import { toggleHideMenu, toggleSideBar } from "../store/reducers/display.reducer";

type AvailableTokenKeys = 'jwt' | 'token' | 'sharing-token';

export function WithLoginToken<T>(WrappedComponent: React.ComponentType<T>) {
    const getExamId = (path: string) => {
        const splitted_path = path.split('/');
        return splitted_path?.[2];
    };

    return function GuestGuardComponent(props: any) {
        const location = useLocation();
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const queryClient = useQueryClient();
        const [isTokenProcessed, setIsTokenProcessed] = useState(false);

        const { setToken, setShareToken } = useAuth();
        const [searchParams] = useSearchParams();

        const token_jwt = searchParams.get('jwt');
        const token = searchParams.get('token');
        const adminToken = searchParams.get('jwt_admin');
        const sharing_token = searchParams.get('sharing-token');

        const source = searchParams.get('source') as undefined | 'app-synchro';
        const renderPolo = searchParams.get('user_type') === 'POLO';

        const handleSource = () => {
            if (source === 'app-synchro') {
                dispatch(toggleHideMenu(true));
                dispatch(toggleSideBar(false));
            } else {
                dispatch(toggleHideMenu(false));
            }
        };

        useEffect(() => {
            const real_token = token ?? token_jwt ?? sharing_token;
            let key: AvailableTokenKeys | null = null;

            if (token_jwt) key = 'jwt';
            if (token) key = 'token';
            if (sharing_token) key = 'sharing-token';

            const redirectPath = location.pathname;

            if (!key) {
                handleSource();
                setIsTokenProcessed(true);
                return;
            }

            if (redirectPath.includes('training') && key !== 'jwt') {
                dispatch(setRestrictedMode(true));
                dispatch(setRestrictedExam(getExamId(redirectPath)));
                if (real_token) setShareToken(real_token);
            } else {
                dispatch(setRestrictedMode(false));
                if (real_token) setToken(real_token);
            }

            if (adminToken) {
                localStorage.setItem('adminToken', adminToken);
            }

            // Clean and preserve query params
            const updatedParams = new URLSearchParams(searchParams.toString());
            updatedParams.delete('jwt');
            updatedParams.delete('token');
            updatedParams.delete('sharing-token');
            updatedParams.delete('jwt_admin');

            if (renderPolo) updatedParams.set('user_type', 'POLO');
            if (source === 'app-synchro') updatedParams.set('source', 'app-synchro');

            // Final redirection URL
            const queryString = updatedParams.toString();
            const newRedirectionPath = `${redirectPath}${queryString ? `?${queryString}` : ''}`;

            queryClient.refetchQueries(['auth']);
            navigate(newRedirectionPath);
            setIsTokenProcessed(true);
        }, [token, token_jwt, sharing_token, adminToken, source, renderPolo]);

        if (!isTokenProcessed) {
            return null;
        }

        return <WrappedComponent {...props} />;
    };
}
